import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { theme, emptyFrame, btnPrimary, inputStyle, type as text } from "../theme";
import { StatusTag } from "./Drawing";
import { DetailSection } from "./RecordView";
import { NextSteps } from "./NextSteps";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { safeHref } from "../utils/safeHref";
import { secondaryBtn } from "../pages/resilience/shared";

// The supplier panel's Compromise tab (docs/TPRM-SUPPLIER-ASSURANCE.md "Supplier compromise", backend
// lib/supplierCompromise.ts). When a supplier may have been compromised, it shows what rests on it from
// the organisation's own records: the assets it delivers and what depends on them, the business impact
// analyses, the processing that names it, the agreements in force, and who an incident recorded with
// these facts would have to tell. Advisories naming the supplier are offered to confirm or dismiss.
// Nothing here raises an incident by itself: "Record an incident" is the ordinary incident register write.

interface ImpactAsset { id: string; name: string; kind: string; criticality: string; isEssentialFunction: boolean; pathText?: string }
interface View {
  supplier: { id: string; name: string; status: string; riskTier: string; isDataProcessor: boolean };
  compromises: Array<{
    id: string; source: "member" | "advisory"; status: "open" | "dismissed" | "closed"; summary: string; reportedAt: string;
    closedAt: string | null; closedReason: string | null; reportedByName: string | null;
    advisory: { id: string; title: string; url: string | null; publishedAt: string } | null;
    incident: { id: string; ref: string; title: string; status: string } | null;
  }>;
  open: string | null;
  impact: {
    deliveredAssets: ImpactAsset[]; businessServices: ImpactAsset[]; dependentAssets: ImpactAsset[]; truncated: boolean;
    bias: Array<{ id: string; ref: string; name: string; status: string; tier: number; mtpdHours: number | null; rtoHours: number | null; namesSupplier: boolean; assets: string[] }>;
    processing: Array<{ id: string; name: string; asProcessor: boolean; sensitive: boolean; systems: string[] }>;
    agreements: Array<{ id: string; title: string; kind: string; standing: string; incidentNotificationHours: number | null }>;
    holdsPersonalData: boolean; sensitiveData: boolean; essentialServiceImpact: boolean; gaps: string[];
  };
  notifications: Array<{ body: string; basis: string; required: boolean; windowHours: number | null }>;
  candidates: Array<{ advisoryId: string; title: string; publishedAt: string; severity: string | null; url: string | null; matched: string }>;
}

const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "4px 10px", fontSize: 12 };
const row: React.CSSProperties = { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, padding: "6px 0", borderBottom: "1px solid var(--c-line)" };
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

function AssetList({ items, empty }: { items: ImpactAsset[]; empty: string }) {
  if (!items.length) return <div style={text.hint}>{empty}</div>;
  return (
    <div>
      {items.map((a) => (
        <div key={a.id} style={row}>
          <span style={text.body}>{a.name}{a.pathText ? <span style={{ ...text.hint, display: "block" }}>{a.pathText}</span> : null}</span>
          <span style={{ ...text.hint, whiteSpace: "nowrap" }}>{a.isEssentialFunction ? "Essential function · " : ""}{a.criticality}</span>
        </div>
      ))}
    </div>
  );
}

export function SupplierCompromisePanel({ supplierId, canWrite }: { supplierId: string; canWrite: boolean }) {
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [summary, setSummary] = useState("");
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    try { setView(await api.get<View>(`/tprm/suppliers/${supplierId}/compromise`)); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load what rests on this supplier.")); }
  }, [supplierId]);
  useEffect(() => { void load(); }, [load]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); await load(); return true; }
    catch (e) { setError(apiErrorMessage(e, "That did not work. Please try again.")); return false; }
    finally { setBusy(false); }
  }

  if (!view) return <div style={text.hint}>{error ?? "Loading…"}</div>;
  const { impact } = view;
  const open = view.compromises.find((c) => c.id === view.open) ?? null;
  const history = view.compromises.filter((c) => c.id !== view.open);

  const report = (advisoryId?: string) => run(async () => {
    await api.post(`/tprm/suppliers/${supplierId}/compromises`, advisoryId ? { advisoryId } : { summary });
    setReporting(false); setSummary("");
  });

  const recordIncident = () => open && run(async () => {
    const { incident } = await api.post<{ incident: { id: string } }>("/resilience/incidents", {
      title: `Possible compromise at ${view.supplier.name}`.slice(0, 200),
      description: open.summary,
      category: "supply_chain", severity: "high", detectedAt: new Date().toISOString(),
      personalDataInvolved: impact.holdsPersonalData, essentialServiceImpact: impact.essentialServiceImpact,
      supplierId, assetIds: impact.deliveredAssets.map((a) => a.id),
    });
    await api.patch(`/tprm/compromises/${open.id}`, { action: "link_incident", incidentId: incident.id });
  });

  return (
    <div>
      {error && <div role="alert" style={{ ...text.hint, color: "var(--c-dangerText)", marginBottom: 12 }}>{error}</div>}

      <NextSteps title="What the records do not say" items={impact.gaps.map((g) => ({ done: false, label: g }))} />

      {/* ── The compromise itself ── */}
      <DetailSection title="Compromise" aside={open ? <StatusTag tone="gap">Open</StatusTag> : <StatusTag tone="none">None open</StatusTag>}>
        {open ? (
          <div>
            <div style={text.body}>{open.summary}</div>
            <div style={{ ...text.hint, marginTop: 4 }}>
              {open.source === "advisory" ? "Confirmed from an advisory" : "Reported"} by {open.reportedByName ?? "a member"} on {fmtDate(open.reportedAt)}
              {open.advisory?.url && <> · <a href={safeHref(open.advisory.url)} target="_blank" rel="noreferrer">the advisory</a></>}
            </div>
            <div style={{ ...text.hint, marginTop: 8 }}>
              {open.incident
                ? <>Recorded as <Link to={`/resilience/incidents?incident=${open.incident.id}`}>{open.incident.ref} {open.incident.title}</Link>.</>
                : "Not yet recorded as an incident. Record one if it affects you; close it with the reason if it does not."}
            </div>
            {canWrite && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                {!open.incident && <button type="button" disabled={busy} onClick={recordIncident} style={smallBtn}>Record an incident</button>}
                {!closing && <button type="button" disabled={busy} onClick={() => { setClosing(true); setReason(""); }} style={smallBtn}>Close</button>}
              </div>
            )}
            {closing && (
              <div style={{ marginTop: 10 }}>
                <label style={text.hint} htmlFor="compromise-close">Why it is closed (for example: the supplier confirmed you were not affected)</label>
                <textarea id="compromise-close" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} style={{ ...inputStyle, width: "100%", marginTop: 4 }} />
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <button type="button" disabled={busy} onClick={async () => { if (await run(() => api.patch(`/tprm/compromises/${open.id}`, { action: "close", reason }))) setClosing(false); }} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }}>Close compromise</button>
                  <button type="button" onClick={() => setClosing(false)} style={smallBtn}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        ) : reporting ? (
          <div>
            <label style={text.hint} htmlFor="compromise-summary">What is known (who told you, what was affected, when)</label>
            <textarea id="compromise-summary" value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} style={{ ...inputStyle, width: "100%", marginTop: 4 }} />
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <button type="button" disabled={busy} onClick={() => report()} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }}>Report possible compromise</button>
              <button type="button" onClick={() => setReporting(false)} style={smallBtn}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span style={text.hint}>Nothing is open. Below is what would be affected if {view.supplier.name} were compromised.</span>
            {canWrite && <button type="button" onClick={() => setReporting(true)} style={smallBtn}>Report possible compromise</button>}
          </div>
        )}
      </DetailSection>

      {/* ── Advisories naming the supplier ── */}
      {view.candidates.length > 0 && (
        <DetailSection title={`Advisories naming ${view.supplier.name}`}>
          {view.candidates.map((c) => (
            <div key={c.advisoryId} style={{ ...row, flexDirection: "column", alignItems: "stretch" }}>
              <div style={text.body}>
                {c.url ? <a href={safeHref(c.url)} target="_blank" rel="noreferrer">{c.title}</a> : c.title}
                <span style={{ ...text.hint, marginLeft: 8 }}>{fmtDate(c.publishedAt)}{c.severity ? ` · ${c.severity}` : ""}</span>
              </div>
              <div style={text.hint}>Named as "{c.matched}". Check whether it concerns the service {view.supplier.name} provides you.</div>
              {canWrite && dismissing !== c.advisoryId && (
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  {!open && <button type="button" disabled={busy} onClick={() => report(c.advisoryId)} style={smallBtn}>It concerns us</button>}
                  <button type="button" disabled={busy} onClick={() => { setDismissing(c.advisoryId); setReason(""); }} style={smallBtn}>It does not</button>
                </div>
              )}
              {dismissing === c.advisoryId && (
                <div style={{ marginTop: 6 }}>
                  <label style={text.hint} htmlFor={`dismiss-${c.advisoryId}`}>Why it does not concern you</label>
                  <textarea id={`dismiss-${c.advisoryId}`} value={reason} onChange={(e) => setReason(e.target.value)} rows={2} style={{ ...inputStyle, width: "100%", marginTop: 4 }} />
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    <button type="button" disabled={busy} onClick={async () => { if (await run(() => api.post(`/tprm/suppliers/${supplierId}/advisories/${c.advisoryId}/dismiss`, { reason }))) setDismissing(null); }} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }}>Dismiss</button>
                    <button type="button" onClick={() => setDismissing(null)} style={smallBtn}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </DetailSection>
      )}

      {/* ── What rests on it ── */}
      <DetailSection title="Assets it delivers" aside={<span style={text.hint}>{plural(impact.deliveredAssets.length, "asset")}</span>}>
        <AssetList items={impact.deliveredAssets} empty="No asset in the register is delivered by this supplier." />
      </DetailSection>
      <DetailSection title="Business services resting on it" aside={impact.truncated ? <span style={text.hint}>Walk stopped at its depth limit</span> : undefined}>
        <AssetList items={impact.businessServices} empty="No business service or essential function rests on its assets." />
        {impact.dependentAssets.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ ...text.hint, fontWeight: 600 }}>Other assets affected</div>
            <AssetList items={impact.dependentAssets} empty="" />
          </div>
        )}
      </DetailSection>
      <DetailSection title="Business impact analyses">
        {impact.bias.length ? impact.bias.map((b) => (
          <div key={b.id} style={row}>
            <span style={text.body}><Link to={`/resilience/bia?bia=${b.id}`}>{b.ref} {b.name}</Link>
              <span style={{ ...text.hint, display: "block" }}>{b.namesSupplier ? "Names this supplier" : `Rests on ${b.assets.join(", ")}`}</span></span>
            <span style={{ ...text.hint, whiteSpace: "nowrap" }}>Tier {b.tier}{b.mtpdHours != null ? ` · tolerable outage ${b.mtpdHours} h` : ""}</span>
          </div>
        )) : <div style={text.hint}>No business impact analysis names this supplier or rests on its assets.</div>}
      </DetailSection>
      <DetailSection title="Personal data" aside={impact.holdsPersonalData ? <StatusTag tone={impact.sensitiveData ? "gap" : "partial"}>{impact.sensitiveData ? "Includes special category data" : "Holds personal data"}</StatusTag> : undefined}>
        {impact.processing.length ? impact.processing.map((p) => (
          <div key={p.id} style={row}>
            <span style={text.body}>{p.name}<span style={{ ...text.hint, display: "block" }}>{p.asProcessor ? "Names it as a processor" : `Runs on ${p.systems.join(", ")}`}</span></span>
            {p.sensitive && <span style={{ ...text.hint, whiteSpace: "nowrap" }}>Special category or criminal offence data</span>}
          </div>
        )) : <div style={text.hint}>{view.supplier.isDataProcessor ? "Marked as a data processor, but no record of processing names it." : "No record of processing names it or runs on its assets."}</div>}
      </DetailSection>
      <DetailSection title="Agreements in force">
        {impact.agreements.length ? impact.agreements.map((a) => (
          <div key={a.id} style={row}>
            <span style={text.body}>{a.title}</span>
            <span style={{ ...text.hint, whiteSpace: "nowrap" }}>{a.incidentNotificationHours != null ? `Must tell you within ${a.incidentNotificationHours} h` : "No notification window"}</span>
          </div>
        )) : <div style={text.hint}>No agreement with this supplier is in force.</div>}
      </DetailSection>
      <DetailSection title="Who an incident would have to tell">
        <div style={{ ...text.hint, marginBottom: 6 }}>
          If this is recorded as an incident with these facts (supply chain, {impact.holdsPersonalData ? "personal data involved" : "no personal data recorded"}), the incident register would raise these. Nothing is raised until an incident is recorded.
        </div>
        {view.notifications.length ? view.notifications.map((n, i) => (
          <div key={i} style={row}>
            <span style={text.body}>{n.body}<span style={{ ...text.hint, display: "block" }}>{n.basis}</span></span>
            <span style={{ ...text.hint, whiteSpace: "nowrap" }}>{n.required ? (n.windowHours != null ? `Required · ${n.windowHours} h` : "Required") : "Recommended"}</span>
          </div>
        )) : <div style={text.hint}>No duty would be raised from the facts the records hold.</div>}
      </DetailSection>

      {history.length > 0 && (
        <DetailSection title="Earlier">
          {history.map((c) => (
            <div key={c.id} style={row}>
              <span style={text.body}>{c.summary}<span style={{ ...text.hint, display: "block" }}>{c.closedReason}</span></span>
              <span style={{ ...text.hint, whiteSpace: "nowrap" }}>{c.status === "dismissed" ? "Dismissed" : "Closed"}{c.closedAt ? ` ${fmtDate(c.closedAt)}` : ""}</span>
            </div>
          ))}
        </DetailSection>
      )}
      {!impact.deliveredAssets.length && !impact.bias.length && !impact.processing.length && (
        <div style={{ ...emptyFrame, marginTop: 12, color: theme.color.muted }}>Link the services this supplier provides as "Delivered by supplier" on the assets to see what would be affected.</div>
      )}
    </div>
  );
}
